/**
 * FilterValueInput — polymorphic value input for a single filter row.
 *
 * Renders the appropriate input control based on the selected field's
 * data type:
 *   - DATE    → GenericDateTimePicker (calendar widget)
 *   - NUMBER  → <input type="number">
 *   - TEXT    → <input type="text">
 *
 * The component receives `type` from the parent FilterRow, which
 * derives it from the CDG header metadata.  When the user changes the
 * selected field, the parent updates `type` and this component swaps
 * the input accordingly.
 *
 * Note on `minDate={undefined}`:
 *   GenericDateTimePicker defaults to `minDate = new Date()` when the
 *   prop is omitted, which blocks past dates.  Filters need to search
 *   historical data, so we explicitly pass `undefined` to remove the
 *   lower bound.
 */
import React from 'react'
import GenericDateTimePicker from '../../../../../components/GenericDateTimePicker'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'

interface FilterValueInputProps {
  type: 'DATE' | 'NUMBER' | 'TEXT'
  name: string
  placeholder?: string
  register: any
}

const FilterValueInput: React.FC<FilterValueInputProps> = ({ type, name, placeholder, register }) => {
  if (type === 'DATE') {
    return (
      // minDate is explicitly undefined to allow past dates.
      // Without this, the date picker would block dates before today,
      // which would prevent filtering on historical section data.
      <GenericDateTimePicker
        name={name}
        placeholder={placeholder}
        allowPastDates={true}
      />
    )
  }
  return (
    <Box>
      <TextField
        type={type === 'NUMBER' ? 'number' : 'text'}
        name={name}
        placeholder={placeholder}
        variant="outlined"
        fullWidth
        size="small"
        {...register(name)}
        sx={{
          mb: 0,
          '& .MuiInputBase-root': {
            minHeight: 42,
            fontSize: 15
          },
          '& .MuiOutlinedInput-input': {
            padding: '10.5px 14px'
          }
        }}
      />
    </Box>
  )
}

export default FilterValueInput
